import { ForbidUnlessAuthorized, PERMISSIONS } from "../../../Auth"
import {
	Breadcrumbs,
	ButtonLink,
	Navigate,
	queryToSearch,
	searchToQuery,
} from "@sembark-travel/ui-router"
import { Helmet } from "react-helmet-async"
import {
	NewQuote,
	RetryOrShowParsingSpinner,
	useTrip,
	ITripQuote,
	JobResponse,
	useAiParsingPolling,
} from "../../../Trips"
import { useParams, generatePath, useLocation } from "../../../router-utils"
import { Alert, Box, Icons, Spinner, Text } from "@sembark-travel/ui-base"
import { useHasFeatureFlag } from "../../../Auth"
import { formatDate } from "@sembark-travel/datetime-utils"
import { childrenToQueryParams } from "../../../Tourists"

export default function TripDetails() {
	const { tripId } = useParams("/trips/:tripId/new-quote")
	const { trip } = useTrip(tripId)
	const location = useLocation()
	const { job_uuid, startDate } = searchToQuery<{
		job_uuid: string
		startDate?: string
	}>(location?.search)
	const hasPackageUploader = useHasFeatureFlag("trip_quote_parsing")

	//here fetching the api to get the status only
	const { status, error, loading } = useAiParsingPolling<
		JobResponse<ITripQuote>
	>({
		job_uuid,
		endpoint: (id) =>
			`/trips/${tripId}/fetch-trip-quote-parsing-status?job_uuid=${id}`,
		enabled: hasPackageUploader && !!job_uuid,
		polling: true,
	})

	// from the status we are fetching the actual data here
	const { data, loading: quoteLoading } = useAiParsingPolling<ITripQuote>({
		job_uuid,
		endpoint: (id) =>
			`/trips/${tripId}/fetch-parsed-trip-quote-data?job_uuid=${id}`,
		enabled: hasPackageUploader && status === "completed" && !!job_uuid,
	})

	const shouldShowParsingState =
		hasPackageUploader &&
		Boolean(job_uuid) &&
		(["pending", "processing", "failed"].includes(status) ||
			quoteLoading ||
			loading)

	const quotedData = data?.quote

	// Update the URL once parsed quote data is available
	if (quotedData && job_uuid && hasPackageUploader && !startDate) {
		return (
			<Navigate
				to={{
					pathname: location.pathname,
					search: queryToSearch({
						startDate: formatDate(quotedData.start_date, "YYYY-MM-DD"),
						days: quotedData.days,
						children: childrenToQueryParams(quotedData.children || []),
						adults: quotedData.no_of_adults,
						job_uuid,
					}),
				}}
				replace
			/>
		)
	}

	if (!trip) {
		return <Spinner alignCenter padding="4" />
	}

	if (trip.is_locked || error?.message) {
		return <Navigate to={generatePath("/trips/:tripId", { tripId })} replace />
	}

	return (
		<>
			<Helmet>
				<title>Create Quote</title>
			</Helmet>
			<ForbidUnlessAuthorized permission={PERMISSIONS.CREATE_QUOTE}>
				<Breadcrumbs
					title="New Quote"
					items={[
						[generatePath("/trips"), "Trips"],
						[generatePath("/trips/:tripId", { tripId }), trip?.name || tripId],
						["", "Create Quote"],
					]}
				/>
				{shouldShowParsingState ? (
					<RetryOrShowParsingSpinner tripId={tripId} status={status} />
				) : (
					<>
						{job_uuid && hasPackageUploader ? (
							<Alert status={!quotedData ? "error" : "info"}>
								<Box
									display="flex"
									justifyContent="between"
									alignItems="center"
								>
									<Text>
										{!quotedData
											? "No itinerary details were found in the uploaded document. Try uploading a different package."
											: "Not satisfied with the generated itinerary? Upload a different package to generate a new one."}
									</Text>
									<ButtonLink to={generatePath("/trips/:tripId", { tripId })}>
										<Icons.Upload /> Upload New Package
									</ButtonLink>
								</Box>
							</Alert>
						) : null}

						<NewQuote initialQuote={data} trip={trip} />
					</>
				)}
			</ForbidUnlessAuthorized>
		</>
	)
}
